import { observer } from 'mobx-react-lite';
import posthog from 'posthog-js';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EFormState } from '@feathr/blackbox';
import { Button, Modal, Text, toast } from '@feathr/components';
import { logUserEvents } from '@feathr/hooks';

import { DomainAvailabilityStates, EmbedFlow, EmbedTypeSelector, useEmbedForm } from './EmbedModal';
import { useDonationFormBuilder } from './useDonationFormBuilder';

import * as styles from './DonationFormEmbedModal.css';

export type EmbedType = 'inline' | 'popup' | undefined;

function DonationFormEmbedModal(): JSX.Element {
  const { t } = useTranslation();
  const {
    donationForm,
    isEmbedModalOpen,
    closeEmbedModal,
    availableContentDomains,
    isLoadingDomains,
  } = useDonationFormBuilder();
  const [embedType, setEmbedType] = useState<EmbedType>();

  const hasAvailableDomains = availableContentDomains ? availableContentDomains.length > 0 : false;

  const { hasAllSnippetsCopied, handleCodeCopied, resetState } = useEmbedForm();

  // Reset modal state when it closes
  useEffect(() => {
    if (isEmbedModalOpen) {
      return;
    }

    setEmbedType(undefined);
    resetState();
  }, [isEmbedModalOpen, resetState]);

  // Publish the form when the modal opens so embedded-form stats start tracking.
  // Previously gated behind a "Generate code" button; now opening the modal is the trigger.
  useEffect(() => {
    if (!isEmbedModalOpen || donationForm.get('state') === EFormState.Published) {
      return;
    }

    async function publish(): Promise<void> {
      try {
        donationForm.set({ state: EFormState.Published });
        await donationForm.patchDirty();
        logUserEvents({ published_fundraising_form: { form_id: donationForm.id } });
        posthog.capture('form_published', {
          account_id: sessionStorage.getItem('accountId'),
          form_id: donationForm.id,
          form_type: 'donation',
        });
      } catch (error) {
        toast(t("Couldn't publish form. Try again."), { type: 'error' });
      }
    }

    publish();
  }, [donationForm, isEmbedModalOpen, t]);

  const handleClose = (): void => {
    closeEmbedModal();
  };

  const description = useMemo(() => {
    if (!embedType) {
      return t(
        "Choose how you'd like your donation form to appear on your website. You can either display it directly on the page (inline) or open it in a pop-up modal when someone clicks a button.",
      );
    }
    return '';
  }, [embedType, t]);

  const modalTitle = useMemo(() => {
    if (embedType === 'inline') {
      return t('Inline Embed');
    }
    if (embedType === 'popup') {
      return t('Pop-up Button');
    }
    return t('Embed your donation form');
  }, [embedType, t]);

  const getModalRightActions = (): JSX.Element[] => {
    if (!embedType) {
      return [
        <Button key={'cancel'} onClick={handleClose}>
          {t('Cancel')}
        </Button>,
      ];
    }

    return [
      <Button key={'cancel'} onClick={handleClose}>
        {t('Cancel')}
      </Button>,
      <Button
        disabled={!hasAllSnippetsCopied(embedType)}
        key={'done'}
        onClick={handleClose}
        type={'primary'}
      >
        {t('Done')}
      </Button>,
    ];
  };

  return (
    <Modal
      onClose={handleClose}
      opened={isEmbedModalOpen}
      rightActions={getModalRightActions()}
      title={modalTitle}
    >
      {description && (
        <Text.DefaultRegular className={styles.modalDescription} truncate={false}>
          {description}
        </Text.DefaultRegular>
      )}

      {/* Step 1: Show embed type selector first */}
      {!embedType && (
        <EmbedTypeSelector
          onSelectInline={(): void => setEmbedType('inline')}
          onSelectPopup={(): void => setEmbedType('popup')}
          selectedType={embedType}
        />
      )}

      {/* Step 2: After embed type is selected, gate on domain availability and show snippet */}
      {embedType && (
        <>
          <DomainAvailabilityStates
            hasAvailableDomains={hasAvailableDomains}
            isLoading={isLoadingDomains}
          />

          {hasAvailableDomains && (
            <EmbedFlow
              donationForm={donationForm}
              embedType={embedType}
              onCodeCopied={handleCodeCopied}
            />
          )}
        </>
      )}
    </Modal>
  );
}

export default observer(DonationFormEmbedModal);
